/**
 * useAppView — URL-driven view-state hook.
 *
 * React Router is the unconditional implementation. View is derived from the
 * URL via React Router and `setView(v)` calls `navigate(slug)`.
 *
 * Single chokepoint that bridges App.tsx's `setView`-everywhere API to URL
 * routing without rewriting any of the 1,200+ lines of App.tsx call sites.
 */

import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type AppView, VIEW_TO_PATH, pathToPropertyId, pathToView } from './slugs';

export function useAppView(initial: AppView = 'overview'): [AppView, (v: AppView, propertyId?: string | number) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const view = pathToView(location.pathname);
  const setView = useCallback(
    (v: AppView, overridePropertyId?: string | number) => {
      const propertyId = overridePropertyId?.toString() ?? pathToPropertyId(location.pathname) ?? undefined;
      const path = VIEW_TO_PATH[v](propertyId);
      navigate(path);
    },
    [location.pathname, navigate],
  );

  // Avoid linter "initial is unused" — preserve param for API parity.
  useEffect(() => {
    void initial;
  }, [initial]);

  return [view, setView];
}

/** Read-only — current `:propertyId` from URL. */
export function useUrlPropertyId(): string | null {
  const location = useLocation();
  return pathToPropertyId(location.pathname);
}
